/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { LONG_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Chip, ChipGroup, Typography2 } from '@coursera/cds-core';

import { ShowMoreGridSection } from 'bundles/cds-labs/components';
import { ArticleCard } from 'bundles/cds-labs/components/Modules/ArticlesCollectionModule/ArticleCard';
import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import ModuleLayout, { getModuleLayoutConfig, noImage } from 'bundles/cds-labs/components/Modules/ModuleLayout';

import _t from 'i18n!nls/cds-labs';

type Article = React.ComponentProps<typeof ArticleCard>;

type ArticleCollectionSet = {
  id: string;
  filter: string;
  articles: Array<Article>;
};

type Props = {
  heading: string;
  description?: string;
  articleCollectionSets: Array<ArticleCollectionSet>;
} & Pick<ModuleLayoutConfig, 'backgroundColor' | 'removeTopPadding' | 'removeBottomPadding'>;

const styles = {
  description: css`
    margin-top: 2px;
  `,

  articlesContainer: css`
    padding-top: var(--cds-spacing-100);
    padding-bottom: 0;
  `,

  chipsContainer: css`
    padding-top: var(--cds-spacing-150);
  `,
};

function ArticlesCollectionModule(props: Props) {
  const { heading, description, articleCollectionSets } = props;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedArticleSet = articleCollectionSets[selectedIndex];

  const renderArticle = (article: Article) => {
    const { publishedDate, estimatedReadingTime, ...rest } = article;
    const metadata: string[] = [];
    if (publishedDate) {
      metadata.push(formatDateTimeDisplay(publishedDate, LONG_DATE_ONLY_DISPLAY));
    }
    if (estimatedReadingTime) {
      metadata.push(estimatedReadingTime);
    }

    return <ArticleCard {...rest} metadata={metadata} />;
  };

  const renderFilterChips = () => {
    const chips = articleCollectionSets?.map(({ id, filter }, idx) => {
      const handleSelect = () => {
        setSelectedIndex(idx);
      };

      return (
        <Chip key={id} selected={idx === selectedIndex} onClick={handleSelect} aria-label={filter}>
          {filter}
        </Chip>
      );
    });

    if (chips.length <= 1) {
      return null;
    }

    return (
      <div css={styles.chipsContainer}>
        <ChipGroup aria-label={_t('Filter By')}>{chips}</ChipGroup>
      </div>
    );
  };

  const renderArticles = () => {
    if (!articleCollectionSets) {
      return null;
    }

    return (
      <ShowMoreGridSection
        // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        key={selectedArticleSet.id}
        // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        items={selectedArticleSet.articles}
        spacing={{
          xs: 8,
          sm: 16,
        }}
        itemsGridConfig={{ xs: 12, md: 3, sm: 6 }}
        defaultRows={1}
        renderItem={({ item }) => renderArticle(item)}
        css={styles.articlesContainer}
      />
    );
  };

  const contentPanel = () => {
    return (
      <div>
        <Typography2 variant="subtitleLarge" component="h2" color="inherit">
          {heading}
        </Typography2>
        {description && (
          <Typography2 variant="bodyPrimary" component="p" color="inherit" css={styles.description}>
            {description}
          </Typography2>
        )}

        {renderFilterChips()}
        {renderArticles()}
      </div>
    );
  };

  const moduleLayoutConfig: ModuleLayoutConfig = getModuleLayoutConfig(props);

  return <ModuleLayout assembly={noImage(moduleLayoutConfig)} renderContent={contentPanel} />;
}

export { ArticlesCollectionModule };

export default ArticlesCollectionModule;

export type { Article, ArticleCollectionSet, Props };
